import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import {
  LogIn, Plus, Loader2, AlertTriangle, X,
} from 'lucide-react';

import { publicApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import BusyBeeLogo from '../components/BusyBeeLogo';
import SectionBlock from '../components/SectionBlock';
import ChangelogView from '../components/ChangelogView';
import { isPrimaryDocLayout, isChangelogLayout } from '../lib/projectLayouts';
import { useEscapeKey } from '../hooks/useEscapeKey';

/**
 * Public team page - `/public/team/:shareToken`.
 *
 * The page deliberately renders a team's published projects through the SAME
 * SectionBlock + view component pipeline as `/app/projects`, just driven in
 * read-only mode. Differences vs the authenticated app:
 *
 *   • Top tabs come from `data.projects` (every project the owner has marked
 *     `public_visibility ∈ {view, contribute}`). Names, ordering and view
 *     types follow whatever the owner configured - no hardcoded Roadmap /
 *     Changelog / Ideas tabs.
 *   • The view type is locked to the project's `layout` (no view switcher).
 *   • For `public_visibility=contribute` projects rendered as gallery, each
 *     card gets an upvote chip and a "Submit idea" button appears in the
 *     toolbar. Anonymous viewers see a sign-in CTA instead.
 */

// ──────────────────────────────────────────────────────────────────────────
// Header
// ──────────────────────────────────────────────────────────────────────────
const PublicTeamHeader = ({ team, viewer, onSignIn }) => {
  const { t } = useTranslation();
  const accentColor = team?.color || '#F59E0B';
  return (
    <header className="border-b border-border bg-background">
      <div className="h-1 w-full" style={{ background: `linear-gradient(90deg, ${accentColor}, #F59E0B)` }} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 flex flex-col sm:flex-row sm:items-start gap-4">
        <div className="flex-1 min-w-0">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-amber-400 text-xs mb-3">
            <BusyBeeLogo className="h-5 w-5" showText={false} />
            <span>{t('publicTeam.builtWith', { defaultValue: 'Built with BusyBee.Day' })}</span>
          </Link>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground truncate">{team?.name}</h1>
          {team?.description && (
            <p className="mt-2 text-sm text-muted-foreground max-w-2xl leading-relaxed">{team.description}</p>
          )}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {viewer?.is_authenticated ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-400" />
              <span>
                {t('publicTeam.signedInAs', {
                  name: viewer.user_name || 'you',
                  defaultValue: 'Signed in as {{name}}',
                })}
              </span>
            </div>
          ) : (
            <button
              onClick={onSignIn}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black text-sm font-medium transition-colors"
            >
              <LogIn className="w-4 h-4" />
              {t('publicTeam.signIn', { defaultValue: 'Sign in' })}
            </button>
          )}
        </div>
      </div>
    </header>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Project tabs (driven by `data.projects` - no hardcoding)
// ──────────────────────────────────────────────────────────────────────────
//
// URL slug for hash routing. Lowercase, ASCII, hyphenated — the same shape
// you'd expect from a Linear-style /public/team/.../#changelog URL.
export const slugifyProjectName = (name) => (name || '')
  .toString()
  .toLowerCase()
  .normalize('NFKD')
  .replace(/[̀-ͯ]/g, '')   // strip combining diacritics
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  || 'project';

const ProjectTabs = ({ projects, activeId, onChange }) => (
  <nav className="border-b border-border bg-background sticky top-0 z-10">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 flex gap-1 overflow-x-auto">
      {projects.map(p => {
        const isActive = activeId === p.id;
        return (
          <button
            key={p.id}
            onClick={() => onChange(p.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${
              isActive
                ? 'border-amber-500 text-amber-400'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <span className="w-2 h-2 rounded-sm shrink-0" style={{ backgroundColor: p.color || '#6B7280' }} />
            {p.name}
          </button>
        );
      })}
    </div>
  </nav>
);

// ──────────────────────────────────────────────────────────────────────────
// Submit-idea modal - minimal payload (project_id, title, description)
// ──────────────────────────────────────────────────────────────────────────
const SubmitIdeaModal = ({ shareToken, projectId, onClose, onSubmitted }) => {
  const { t } = useTranslation();
  useEscapeKey(onClose);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = title.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      await publicApi.submitIdea(shareToken, {
        project_id: projectId,
        title: trimmed,
        description: description.trim() || null,
      });
      toast.success(t('publicTeam.ideas.submitted', { defaultValue: 'Idea submitted - thank you!' }));
      await onSubmitted();
    } catch (err) {
      const code = err.response?.status;
      if (code === 429) toast.error(t('publicTeam.ideas.rateLimited', { defaultValue: 'Slow down a little - please try again later.' }));
      else if (code === 401) toast.error(t('publicTeam.ideas.signInRequired', { defaultValue: 'Please sign in to submit an idea.' }));
      else toast.error(t('publicTeam.ideas.submitFailed', { defaultValue: 'Could not submit your idea. Please try again.' }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="text-base font-semibold text-foreground">
            {t('publicTeam.ideas.submitTitle', { defaultValue: 'Share your idea' })}
          </h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">
              {t('publicTeam.ideas.titleLabel', { defaultValue: 'Title' })}
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              maxLength={200}
              autoFocus
              placeholder={t('publicTeam.ideas.titlePlaceholder', { defaultValue: 'A short, clear summary' })}
              className="w-full bg-background border border-input rounded-md px-3 py-2 text-sm focus:border-amber-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">
              {t('publicTeam.ideas.descriptionLabel', { defaultValue: 'Description' })}
              <span className="text-muted-foreground/60 font-normal"> · {t('publicTeam.ideas.optional', { defaultValue: 'optional' })}</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              maxLength={5000}
              placeholder={t('publicTeam.ideas.descriptionPlaceholder', { defaultValue: 'Add details, context, or links.' })}
              className="w-full bg-background border border-input rounded-md px-3 py-2 text-sm focus:border-amber-500 focus:outline-none resize-none"
            />
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground">
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </button>
            <button
              type="submit"
              disabled={submitting || !title.trim()}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {t('publicTeam.ideas.submit', { defaultValue: 'Submit idea' })}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Page wrapper
// ──────────────────────────────────────────────────────────────────────────
const PublicTeamPage = () => {
  const { shareToken } = useParams();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitOpenForProject, setSubmitOpenForProject] = useState(null);

  const fetchData = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await publicApi.getTeam(shareToken);
      setData(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.status === 404 ? 'not_found' : 'generic');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [shareToken]);

  useEffect(() => { fetchData(); }, [fetchData, isAuthenticated]);

  // The active tab is derived from location.hash (with a first-project
  // fallback). Keeping the hash as the single source of truth avoids the
  // double-render flicker that came from holding active-tab state in parallel
  // with the URL and trying to sync them in an effect.
  const activeProjectId = useMemo(() => {
    if (!data?.projects?.length) return null;
    const slug = (location.hash || '').replace(/^#/, '').toLowerCase();
    if (slug) {
      const match = data.projects.find(p => slugifyProjectName(p.name) === slug);
      if (match) return match.id;
    }
    return data.projects[0].id;
  }, [data, location.hash]);

  // Tab click: just push the new hash. The next render reads it via the
  // memo above - no parallel state to keep in sync.
  const handleTabChange = useCallback((projectId) => {
    const project = data?.projects?.find(p => p.id === projectId);
    if (!project) return;
    const slug = slugifyProjectName(project.name);
    if (location.hash.replace(/^#/, '') !== slug) {
      navigate(`${location.pathname}#${slug}`, { replace: false });
    }
  }, [data, location.pathname, location.hash, navigate]);

  const handleSignInRequired = useCallback(() => {
    const redirect = `${location.pathname}${location.search}${location.hash}`;
    navigate(`/login?redirect=${encodeURIComponent(redirect)}`);
  }, [navigate, location]);

  const activeProject = useMemo(
    () => data?.projects?.find(p => p.id === activeProjectId) || null,
    [data, activeProjectId],
  );

  const tasksByProject = useMemo(() => {
    const map = new Map();
    for (const t of data?.tasks || []) {
      if (!map.has(t.project_id)) map.set(t.project_id, []);
      map.get(t.project_id).push(t);
    }
    return map;
  }, [data]);

  const handleUpvote = useCallback(async (taskId) => {
    if (!isAuthenticated) {
      handleSignInRequired();
      return;
    }
    try {
      await publicApi.upvoteIdea(shareToken, taskId);
      await fetchData({ silent: true });
    } catch (err) {
      if (err.response?.status === 401) handleSignInRequired();
      else toast.error(t('publicTeam.ideas.upvoteFailed', { defaultValue: 'Could not record your vote. Please try again.' }));
    }
  }, [isAuthenticated, handleSignInRequired, shareToken, fetchData, t]);

  // ── Render ────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-amber-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-4 opacity-70" />
          <h1 className="text-xl font-semibold text-foreground mb-2">
            {error === 'not_found'
              ? t('publicTeam.errors.notFoundTitle', { defaultValue: 'This page is no longer shared' })
              : t('publicTeam.errors.genericTitle', { defaultValue: 'Something went wrong' })}
          </h1>
          <p className="text-sm text-muted-foreground">
            {error === 'not_found'
              ? t('publicTeam.errors.notFoundBody', { defaultValue: 'The link may have been revoked by the team.' })
              : t('publicTeam.errors.genericBody', { defaultValue: 'Please refresh the page or try again shortly.' })}
          </p>
          <Link to="/" className="mt-5 inline-block text-sm text-amber-400 hover:text-amber-300">
            ← {t('publicTeam.errors.backHome', { defaultValue: 'Back to BusyBee.Day' })}
          </Link>
        </div>
      </div>
    );
  }

  const { team, viewer, sections, projects } = data;
  const tasksOfActive = tasksByProject.get(activeProjectId) || [];
  const sectionsOfActive = (sections || []).filter(s => s.project_id === activeProjectId);
  const isContribute = activeProject?.public_visibility === 'contribute';
  const isChangelog = isChangelogLayout(activeProject?.layout);
  const isPrimaryDoc = isPrimaryDocLayout(activeProject?.layout);
  const seedViewType = activeProject?.layout || 'list';
  const changelogDocs = (data.documents || []).filter(d => d.project_id === activeProjectId && d.type === 'changelog');

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <Helmet>
        <title>{team.name}</title>
        {team.description && <meta name="description" content={team.description} />}
        <meta property="og:title" content={team.name} />
        {team.description && <meta property="og:description" content={team.description} />}
        <meta property="og:type" content="website" />
      </Helmet>

      <PublicTeamHeader team={team} viewer={viewer} onSignIn={handleSignInRequired} />

      {projects.length === 0 ? (
        <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 py-16 text-center text-muted-foreground">
          <p className="text-sm">
            {t('publicTeam.empty_no_projects', {
              defaultValue: "This team hasn't published any projects yet.",
            })}
          </p>
        </main>
      ) : (
        <>
          <ProjectTabs
            projects={projects}
            activeId={activeProjectId}
            onChange={handleTabChange}
          />
          <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 py-6">
            {activeProject && isChangelog ? (
              <ChangelogView documents={changelogDocs} readOnly />
            ) : activeProject && isPrimaryDoc ? (
              // Primary-doc layouts (whiteboard, document, database…) center
              // on a single document. Public read-only embedding of those
              // editors is out of scope for v1 - point the visitor at a sign-in.
              <div className="text-center py-16 border border-dashed border-border rounded-lg">
                <p className="text-sm text-muted-foreground">
                  {t('publicTeam.primary_doc_hidden', {
                    defaultValue: 'A read-only preview of this content is not available yet.',
                  })}
                </p>
                <button
                  onClick={handleSignInRequired}
                  className="mt-4 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black text-sm font-medium transition-colors"
                >
                  {t('publicTeam.signIn', { defaultValue: 'Sign in' })}
                </button>
              </div>
            ) : activeProject && (
              <>
                {/* Toolbar - Submit idea CTA on contribute projects */}
                {isContribute && (
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <p className="text-sm text-muted-foreground">
                      {t('publicTeam.ideas.subtitle', {
                        defaultValue: 'Vote for what matters and share your own suggestions.',
                      })}
                    </p>
                    {viewer.is_authenticated ? (
                      <button
                        onClick={() => setSubmitOpenForProject(activeProject.id)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black text-sm font-medium transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                        {t('publicTeam.ideas.submit', { defaultValue: 'Submit idea' })}
                      </button>
                    ) : (
                      <button
                        onClick={handleSignInRequired}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-amber-500/50 text-amber-400 hover:bg-amber-500/10 text-sm font-medium transition-colors"
                      >
                        <LogIn className="w-4 h-4" />
                        {t('publicTeam.ideas.signInToContribute', {
                          defaultValue: 'Sign in to submit',
                        })}
                      </button>
                    )}
                  </div>
                )}

                {/* If the project has sections (typical for board layouts split
                    into sub-lists), render a SectionBlock per section.
                    Otherwise render one virtual section that holds all tasks. */}
                {sectionsOfActive.length > 0 ? (
                  <div className="space-y-6">
                    {sectionsOfActive.map(section => (
                      <SectionBlock
                        key={section.id}
                        section={section}
                        tasks={tasksOfActive.filter(tk => tk.section_id === section.id)}
                        projects={[activeProject]}
                        selectedProject={activeProject}
                        selectedProjectId={activeProject.id}
                        customFieldDefs={[]}
                        onTaskUpdate={() => {}}
                        header={
                          <span className="text-[13px] font-semibold text-foreground">
                            {section.name}
                          </span>
                        }
                        readOnly
                        seedViewType={seedViewType}
                        showUpvotes={isContribute && seedViewType === 'gallery'}
                        onUpvote={handleUpvote}
                        onSignInRequired={handleSignInRequired}
                        isAuthenticated={viewer.is_authenticated}
                        statusConfigs={data.task_statuses}
                      />
                    ))}
                  </div>
                ) : (
                  <SectionBlock
                    section={{
                      id: `public-${activeProject.id}`,
                      project_id: activeProject.id,
                      name: activeProject.name,
                    }}
                    tasks={tasksOfActive.filter(tk => !tk.section_id)}
                    projects={[activeProject]}
                    selectedProject={activeProject}
                    selectedProjectId={activeProject.id}
                    customFieldDefs={[]}
                    onTaskUpdate={() => {}}
                    header={null}
                    readOnly
                    seedViewType={seedViewType}
                    showUpvotes={isContribute && seedViewType === 'gallery'}
                    onUpvote={handleUpvote}
                    onSignInRequired={handleSignInRequired}
                    isAuthenticated={viewer.is_authenticated}
                    statusConfigs={data.task_statuses}
                  />
                )}
              </>
            )}
          </main>
        </>
      )}

      <footer className="border-t border-border bg-background">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between text-xs text-muted-foreground">
          <Link to="/" className="flex items-center gap-2 hover:text-amber-400 transition-colors">
            <BusyBeeLogo className="h-4 w-4" showText={false} />
            {t('publicTeam.builtWith', { defaultValue: 'Built with BusyBee.Day' })}
          </Link>
          <Link to="/register" className="hover:text-amber-400 transition-colors">
            {t('publicTeam.startOwn', { defaultValue: 'Start your own →' })}
          </Link>
        </div>
      </footer>

      {submitOpenForProject && (
        <SubmitIdeaModal
          shareToken={shareToken}
          projectId={submitOpenForProject}
          onClose={() => setSubmitOpenForProject(null)}
          onSubmitted={async () => {
            setSubmitOpenForProject(null);
            await fetchData({ silent: true });
          }}
        />
      )}
    </div>
  );
};

export default PublicTeamPage;
